import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export interface ScopingHeaderProps {
  /** Project name to display */
  projectName: string
  /** Current version number */
  version: number
  /** Status text to display (e.g., "Scoping V1" or undefined for badge) */
  statusText?: string
  /** Badge to display (e.g., "Completed") */
  badge?: {
    label: string
    variant: 'success' | 'warning' | 'info'
  }
  /** Optional right-side actions */
  actions?: React.ReactNode
  /** Custom back handler (defaults to /dashboard) */
  onBack?: () => void
  /** Back button destination for aria-label */
  backLabel?: string
}

const BADGE_VARIANTS = {
  success: 'bg-[#DCFCE7] text-[#166534]',
  warning: 'bg-[#FEF3C7] text-[#92400E]',
  info: 'bg-[#DBEAFE] text-[#1E40AF]',
}

/**
 * Unified header component for Scoping V2 pages
 * Provides consistent navigation and project context
 */
export function ScopingHeader({
  projectName,
  version,
  statusText,
  badge,
  actions,
  onBack,
  backLabel,
}: ScopingHeaderProps) {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack()
    } else {
      navigate('/dashboard')
    }
  }, [onBack, navigate])

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-background px-4 sm:h-16 sm:px-8 transition-all duration-200">
      {/* Left: Back button + Project info */}
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
        {/* Back button with hover animation */}
        <button
          onClick={handleBack}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background transition-all duration-150 hover:bg-background-secondary hover:border-foreground-subtle active:scale-95 sm:h-9 sm:w-9"
          aria-label={backLabel || t('common.back')}
        >
          <ArrowLeft className="h-4 w-4 text-foreground transition-transform duration-150 group-hover:-translate-x-0.5 sm:h-[18px] sm:w-[18px]" />
        </button>

        {/* Project info with fade-in animation */}
        <div className="flex min-w-0 flex-col gap-0.5 animate-in fade-in slide-in-from-left-2 duration-200 sm:gap-1">
          <span className="truncate text-sm font-semibold text-foreground sm:text-base">
            {projectName}
          </span>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Status badge */}
            {badge && (
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors sm:px-2 sm:text-xs',
                  BADGE_VARIANTS[badge.variant]
                )}
              >
                {badge.label}
              </span>
            )}
            {/* Status text */}
            <span className="text-xs text-foreground-muted sm:text-[13px]">
              {statusText || t('scopingV2.header.status', { version })}
            </span>
          </div>
        </div>
      </div>

      {/* Right: Action buttons with staggered animation */}
      {actions && (
        <div className="flex shrink-0 items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-200 delay-75 sm:gap-3">
          {actions}
        </div>
      )}
    </header>
  )
}
